/*
 * Presento Pronto: Liefer-Zeile im Warenkorb <pp-cart-boost> (Markup in snippets/pp-cart-boost.liquid).
 *
 * Warum ein eigenes Modul statt {% javascript %}: Shopify fuehrt Snippet-JavaScript nur aus, wenn das
 * Snippet beim Seitenaufruf gerendert wurde. War der Warenkorb beim Laden leer, fehlte das Element und
 * damit auch seine Element-Definition, wenn der Drawer spaeter (z. B. nach Quick-Add) per Morph neu
 * aufgebaut wurde. Das Modul wird ueber {% render 'pp-cart-boost', assets_only: true %} an einer
 * immer gerenderten Stelle geladen.
 *
 * Aktualisierung:
 * - sobald der Liefer-Kern bereit ist (window.ppDelivery bzw. Event 'pp:delivery-ready'),
 * - nach jeder Warenkorb-Aenderung: Horizon feuert 'shopify:cart:lines-update' (StandardEvents.cartLinesUpdate
 *   aus @shopify/events) und morpht danach den Drawer. Der Morph behaelt dieses Element, setzt aber die
 *   Kinder auf den Server-Fallback zurueck. Ein MutationObserver auf das eigene Element rechnet danach
 *   sofort neu. Waehrend update() ist der Observer getrennt, eigene Aenderungen loesen also keine Schleife aus.
 * - alle 30 Sekunden (Countdown) und beim Zurueckkehren in den Tab.
 */

const CART_LINES_UPDATE = 'shopify:cart:lines-update';
const DELIVERY_READY = 'pp:delivery-ready';
const OBSERVE_OPTIONS = {
  childList: true,
  subtree: true,
  characterData: true,
  attributes: true,
  attributeFilter: [
    'hidden',
    'data-cutoff',
    'data-production',
    'data-transit-min',
    'data-transit-max',
    'data-ship-saturday',
    'data-deliver-saturday',
    'data-holidays',
    'data-tpl-eta',
    'data-tpl-eta-plain',
    'data-tpl-occasion',
  ],
};

if (!customElements.get('pp-cart-boost')) {
  class PpCartBoost extends HTMLElement {
    connectedCallback() {
      /* Idempotent: falls connectedCallback erneut laeuft, erst alles Alte abbauen */
      this.teardown();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      if (window.ppDelivery) {
        this.start();
      } else {
        document.addEventListener(DELIVERY_READY, () => this.start(), { once: true, signal });
      }
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
      clearInterval(this.timer);
      this.timer = null;
      if (this.observer) this.observer.disconnect();
      this.observer = null;
    }

    start() {
      if (!this.isConnected || !this.abortController || !window.ppDelivery) return;
      const { signal } = this.abortController;

      /* MutationObserver buendelt alle Aenderungen eines Morphs ohnehin zu einem Callback */
      this.observer = new MutationObserver(() => this.update());
      this.update();

      this.timer = setInterval(() => this.update(), 30000);
      document.addEventListener(CART_LINES_UPDATE, () => this.update(), { signal });
      document.addEventListener(
        'visibilitychange',
        () => {
          if (!document.hidden) this.update();
        },
        { signal }
      );
    }

    update() {
      const core = window.ppDelivery;
      if (!core || !this.isConnected) return;

      /* Eigene Aenderungen nicht beobachten (keine Endlosschleife) */
      if (this.observer) this.observer.disconnect();
      try {
        this.render(core);
      } finally {
        if (this.observer && this.isConnected) this.observer.observe(this, OBSERVE_OPTIONS);
      }
    }

    render(core) {
      const slot = this.querySelector('[data-slot="eta"]');
      if (!slot) return;

      const result = core.compute(core.readConfig(this));
      const minutes = result.minutesToCutoff ?? result.minutesLeft;
      const { tplEta = '', tplEtaPlain = '', tplOccasion = '' } = this.dataset;
      /* Countdown nur, wenn der Bestellschluss innerhalb von 24 Stunden liegt ("noch 40 Std." am Wochenende hilft niemandem) */
      const template = minutes > 0 && minutes <= 1440 && tplEta.includes('{countdown}') ? tplEta : tplEtaPlain;
      if (!template.includes('{when}')) return;

      const hit = core.activeOccasion ? core.activeOccasion(result) : null;
      const when = document.createElement('strong');
      if (hit && tplOccasion) {
        when.className = 'pp-cb__good';
        when.textContent = tplOccasion.replace('{occasion}', hit.label);
      } else {
        when.textContent = core.formatRange(result.earliest, result.latest);
      }

      /* Teile in Reihenfolge des Templates zusammensetzen: {countdown} und {when} fett */
      const parts = template.split(/(\{countdown\}|\{when\})/).map((part) => {
        if (part === '{when}') return when;
        if (part === '{countdown}') {
          const strong = document.createElement('strong');
          strong.textContent = core.formatCountdown(minutes);
          return strong;
        }
        return part;
      });

      const next = parts.map((part) => (typeof part === 'string' ? part : part.textContent)).join('');
      if (slot.textContent !== next) slot.replaceChildren(...parts);
    }
  }

  customElements.define('pp-cart-boost', PpCartBoost);
}
